/**
 * Supabase Service — API Proxy Client
 * Project Risk & Complexity Assessment
 *
 * All data operations route through the Cloudflare Pages Function
 * at /api/supabase — never directly to Supabase from the browser.
 *
 * Includes an offline save queue: failed saves are stored in
 * localStorage under 'rca_save_queue' and retried on the next call.
 *
 * @version 2.0.0
 */

import type {
  RCAProject,
  AssessmentSummary,
  SupabaseAssessment,
  HistorySnapshot,
  ShareToken,
  Notification,
  UserSettings,
  ProjectDocument,
} from '../types/rca';
import { supabase } from '@/lib/supabase';

// ============================================================
// Constants
// ============================================================

const API_ENDPOINT = '/api/supabase';
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 1000;
const SAVE_QUEUE_KEY = 'rca_save_queue';

// ============================================================
// Types
// ============================================================

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

interface QueuedRequest {
  action: string;
  params: Record<string, unknown>;
  queuedAt: string;
}

// ============================================================
// Internal helpers
// ============================================================

async function getAuthToken(): Promise<string> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? '';
  } catch {
    return '';
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function apiCall<T = unknown>(
  action: string,
  params: Record<string, unknown> = {},
  retries = MAX_RETRIES
): Promise<ApiResponse<T>> {
  const token = await getAuthToken();
  if (!token) {
    return { success: false, error: 'Not authenticated.' };
  }

  const body = { ...params, action };

  try {
    const res = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (res.ok) {
      const json = await res.json() as ApiResponse<T>;
      return json;
    }

    // Retry on 5xx
    if (res.status >= 500 && retries > 0) {
      await delay(RETRY_BASE_DELAY_MS * (MAX_RETRIES - retries + 1));
      return apiCall<T>(action, params, retries - 1);
    }

    const errJson = await res.json().catch(() => ({})) as { error?: string };
    return { success: false, error: errJson.error ?? `HTTP ${res.status}` };
  } catch (err) {
    if (retries > 0) {
      await delay(RETRY_BASE_DELAY_MS * (MAX_RETRIES - retries + 1));
      return apiCall<T>(action, params, retries - 1);
    }
    const message = err instanceof Error ? err.message : 'Network error';
    return { success: false, error: `Network error. Working offline. (${message})` };
  }
}

// ============================================================
// Offline Save Queue
// ============================================================

function readQueue(): QueuedRequest[] {
  try {
    const raw = localStorage.getItem(SAVE_QUEUE_KEY);
    return raw ? (JSON.parse(raw) as QueuedRequest[]) : [];
  } catch {
    return [];
  }
}

function writeQueue(queue: QueuedRequest[]): void {
  try {
    localStorage.setItem(SAVE_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // Storage full — can't persist queue
  }
}

function enqueueRequest(action: string, params: Record<string, unknown>): void {
  const queue = readQueue();
  queue.push({ action, params, queuedAt: new Date().toISOString() });
  writeQueue(queue);
}

async function replayQueue(): Promise<void> {
  const queue = readQueue();
  if (queue.length === 0) return;

  const remaining: QueuedRequest[] = [];
  for (const req of queue) {
    const result = await apiCall(req.action, req.params, 1);
    if (!result.success) {
      remaining.push(req);
    }
  }
  writeQueue(remaining);
}

// ============================================================
// Projects
// ============================================================

export async function listProjects(): Promise<RCAProject[]> {
  await replayQueue();
  const result = await apiCall<RCAProject[]>('list-projects', {});
  if (result.success && result.data) return result.data;
  throw new Error(result.error ?? 'Failed to list projects');
}

export async function getProject(id: string): Promise<RCAProject | null> {
  if (!id) throw new Error('Project ID is required');
  const result = await apiCall<RCAProject>('get-project', { id });
  if (result.success) return result.data ?? null;
  throw new Error(result.error ?? 'Failed to get project');
}

export async function saveProject(project: Partial<RCAProject>): Promise<RCAProject> {
  if (!project) throw new Error('Project data is required');

  const toSave: Partial<RCAProject> = {
    ...project,
    updated_at: new Date().toISOString(),
  };
  if (!toSave.id) {
    toSave.created_at = toSave.updated_at;
  }

  await replayQueue();
  const result = await apiCall<RCAProject | RCAProject[]>('save-project', { project: toSave });

  if (result.success && result.data) {
    const saved = Array.isArray(result.data) ? result.data[0] : result.data;
    return saved;
  }

  // Offline: enqueue for later retry
  enqueueRequest('save-project', { project: toSave });
  throw new Error(result.error ?? 'Failed to save project (queued for retry)');
}

export async function deleteProject(id: string): Promise<boolean> {
  if (!id) throw new Error('Project ID is required');
  const result = await apiCall<boolean>('delete-project', { id });
  if (result.success) return true;
  throw new Error(result.error ?? 'Failed to delete project');
}

// ============================================================
// Assessments
// ============================================================

export async function listAssessments(projectId: string): Promise<AssessmentSummary[]> {
  if (!projectId) throw new Error('Project ID is required');
  await replayQueue();
  const result = await apiCall<AssessmentSummary[]>('list-assessments', { project_id: projectId });
  if (result.success && result.data) return result.data;
  throw new Error(result.error ?? 'Failed to list assessments');
}

export async function listAllAssessments(): Promise<AssessmentSummary[]> {
  await replayQueue();
  const result = await apiCall<AssessmentSummary[]>('list-assessments', {});
  if (result.success && result.data) return result.data;
  throw new Error(result.error ?? 'Failed to list assessments');
}

export async function getAssessment(id: string): Promise<SupabaseAssessment | null> {
  if (!id) throw new Error('Assessment ID is required');
  const result = await apiCall<SupabaseAssessment>('get-assessment', { id });
  if (result.success) return result.data ?? null;
  throw new Error(result.error ?? 'Failed to get assessment');
}

export async function saveAssessment(
  assessment: Partial<SupabaseAssessment>
): Promise<SupabaseAssessment> {
  if (!assessment) throw new Error('Assessment data is required');

  const toSave: Partial<SupabaseAssessment> = {
    ...assessment,
    updated_at: new Date().toISOString(),
  };
  if (!toSave.id) {
    toSave.created_at = toSave.updated_at;
  }

  await replayQueue();
  const result = await apiCall<SupabaseAssessment | SupabaseAssessment[]>(
    'save-assessment',
    { assessment: toSave }
  );

  if (result.success && result.data) {
    const saved = Array.isArray(result.data) ? result.data[0] : result.data;
    return saved;
  }

  // Offline: enqueue for later retry
  enqueueRequest('save-assessment', { assessment: toSave });
  throw new Error(result.error ?? 'Failed to save assessment (queued for retry)');
}

// ============================================================
// History
// ============================================================

export async function saveHistory(snapshot: HistorySnapshot): Promise<void> {
  if (!snapshot) return;
  // Fire-and-forget: log failure but don't throw
  const result = await apiCall('save-history', { snapshot });
  if (!result.success) {
    console.warn('supabaseService: History save failed:', result.error);
  }
}

export async function getHistory(projectId: string): Promise<HistorySnapshot[]> {
  if (!projectId) return [];
  const result = await apiCall<HistorySnapshot[]>('get-history', { project_id: projectId });
  if (result.success && result.data) return result.data;
  throw new Error(result.error ?? 'Failed to get history');
}

// ============================================================
// Users
// ============================================================

export async function upsertUser(
  email: string,
  displayName?: string
): Promise<void> {
  if (!email) throw new Error('Email is required');
  const result = await apiCall('upsert-user', { email, display_name: displayName });
  if (!result.success) {
    throw new Error(result.error ?? 'Failed to upsert user');
  }
}

// ============================================================
// Notifications
// ============================================================

export async function listNotifications(email: string): Promise<Notification[]> {
  if (!email) return [];
  const result = await apiCall<Notification[]>('list-notifications', { email });
  if (result.success && result.data) return result.data;
  throw new Error(result.error ?? 'Failed to list notifications');
}

export async function markNotificationRead(id: string): Promise<void> {
  if (!id) return;
  // Fire-and-forget
  const result = await apiCall('mark-notification-read', { id });
  if (!result.success) {
    console.warn('supabaseService: Mark notification read failed:', result.error);
  }
}

// ============================================================
// Share Tokens
// ============================================================

export async function saveShareToken(
  assessmentId: string,
  expiresAt: string
): Promise<ShareToken> {
  if (!assessmentId) throw new Error('Assessment ID is required');
  if (!expiresAt) throw new Error('Expiration date is required');

  // created_by is populated server-side from the verified JWT identity
  const tokenData: Omit<ShareToken, 'created_at'> = {
    token: crypto.randomUUID(),
    project_id: '',   // Server will populate from assessmentId
    assessment_id: assessmentId,
    expires_at: expiresAt,
    created_by: '',
  };

  const result = await apiCall<ShareToken>('save-share-token', { token_data: tokenData });
  if (result.success && result.data) return result.data;
  throw new Error(result.error ?? 'Failed to save share token');
}

export async function validateShareToken(token: string): Promise<SupabaseAssessment | null> {
  if (!token) return null;
  const result = await apiCall<SupabaseAssessment>('validate-share-token', { token });
  if (result.success && result.data) return result.data;
  return null;
}

export interface ShareViewPayload {
  project: { id: string; name: string; department: string; owner: string; description: string } | null;
  assessment: SupabaseAssessment | null;
  expires_at: string;
}

/** Public — no auth required. Calls /api/share directly, bypassing the session guard. */
export async function validateShareTokenPublic(token: string): Promise<ShareViewPayload | null> {
  if (!token) return null;
  try {
    const res = await fetch('/api/share', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'validate', token }),
    });
    if (!res.ok) return null;
    const json = await res.json() as ApiResponse<ShareViewPayload>;
    if (json.success && json.data) return json.data;
    return null;
  } catch {
    return null;
  }
}

// ============================================================
// Settings
// ============================================================

export async function getSettings(key: string): Promise<unknown> {
  if (!key) throw new Error('Settings key is required');
  const result = await apiCall<UserSettings>('get-settings', { key });
  if (result.success && result.data) return result.data.value;
  return null;
}

export async function saveSettings(key: string, value: unknown): Promise<void> {
  if (!key) throw new Error('Settings key is required');
  const result = await apiCall('save-settings', { key, value });
  if (!result.success) {
    throw new Error(result.error ?? 'Failed to save settings');
  }
}

// ============================================================
// Documents
// ============================================================

export async function listDocuments(projectId: string): Promise<ProjectDocument[]> {
  if (!projectId) throw new Error('Project ID is required');
  const result = await apiCall<ProjectDocument[]>('list-documents', { project_id: projectId });
  if (result.success && result.data) return result.data;
  throw new Error(result.error ?? 'Failed to list documents');
}

export async function getDocument(id: string): Promise<ProjectDocument | null> {
  if (!id) throw new Error('Document ID is required');
  const result = await apiCall<ProjectDocument>('get-document', { id });
  if (result.success) return result.data ?? null;
  throw new Error(result.error ?? 'Failed to get document');
}

